import type { Nil } from '@pbt/pbt-ui-components'

import type { ExtendPayment } from '~/types'
import { getPaymentInvoiceIds } from '~/utils/paymentUtils'
import { trackEvent, TrackingEvent } from '~/utils/trackingProvider'

function getInvoiceId(payment?: ExtendPayment | Nil): string | undefined {
  const invoiceIds = getPaymentInvoiceIds(payment)
  return invoiceIds.length > 0 ? invoiceIds[0] : undefined
}

function getInvoiceType(payment?: ExtendPayment | Nil): string | undefined {
  if (payment?.invoices && payment.invoices.length > 0) {
    const firstInvoice = payment.invoices[0]
    return (
      firstInvoice.invoiceType ??
      (firstInvoice.type as string | undefined) ??
      undefined
    )
  }
  return undefined
}

export interface TrackReverseTransactionStartedParams {
  businessId: string
  clientId?: string | null
  payment: ExtendPayment | Nil
}

export function trackReverseTransactionStarted({
  payment,
  businessId,
  clientId,
}: TrackReverseTransactionStartedParams): void {
  trackEvent(TrackingEvent.ADD_PAYMENT_V2_REVERSE_TRANSACTION_STARTED, {
    timestamp: Date.now(),
    invoiceId: getInvoiceId(payment),
    invoiceType: getInvoiceType(payment),
    businessId,
    customerId: clientId || payment?.clientId || null,
    paymentId: payment?.id || null,
  })
}

export interface TrackReverseTransactionProcessingParams {
  businessId: string
  clientId?: string | null
  payment: ExtendPayment | Nil
  transactionId: string
}

export function trackReverseTransactionProcessing({
  payment,
  businessId,
  clientId,
  transactionId,
}: TrackReverseTransactionProcessingParams): void {
  trackEvent(TrackingEvent.ADD_PAYMENT_V2_REVERSE_TRANSACTION_PROCESSING, {
    timestamp: Date.now(),
    invoiceId: getInvoiceId(payment),
    invoiceType: getInvoiceType(payment),
    businessId,
    customerId: clientId || payment?.clientId || null,
    transactionId,
    paymentId: payment?.id || null,
  })
}

export interface TrackReverseSuccessParams {
  businessId: string
  clientId?: string | null
  payment: ExtendPayment | Nil
  processingDurationMs?: number
  reverseAmount: number
  transactionId: string
}

export function trackReverseSuccess({
  payment,
  businessId,
  clientId,
  reverseAmount,
  transactionId,
  processingDurationMs,
}: TrackReverseSuccessParams): void {
  trackEvent(TrackingEvent.ADD_PAYMENT_V2_REVERSE_SUCCESS, {
    timestamp: Date.now(),
    invoiceId: getInvoiceId(payment),
    invoiceType: getInvoiceType(payment),
    businessId,
    customerId: clientId || payment?.clientId || null,
    transactionId,
    paymentId: payment?.id || null,
    processingDurationMs,
    reverseAmount,
  })
}

export interface TrackReverseFailureParams {
  businessId: string
  clientId?: string | null
  errorMessage?: string | null
  failureReason?: string | null
  payment: ExtendPayment | Nil
  processingDurationMs?: number
  reverseAmount?: number
  transactionId: string
}

export function trackReverseFailure({
  payment,
  businessId,
  clientId,
  transactionId,
  reverseAmount,
  failureReason,
  errorMessage,
  processingDurationMs,
}: TrackReverseFailureParams): void {
  trackEvent(TrackingEvent.ADD_PAYMENT_V2_REVERSE_FAILURE, {
    timestamp: Date.now(),
    invoiceId: getInvoiceId(payment),
    invoiceType: getInvoiceType(payment),
    businessId,
    customerId: clientId || payment?.clientId || null,
    transactionId,
    paymentId: payment?.id || null,
    processingDurationMs,
    ...(reverseAmount !== undefined && { reverseAmount }),
    failureReason: failureReason || null,
    errorMessage: errorMessage || null,
  })
}

export interface TrackReverseInitiationErrorParams {
  businessId: string
  clientId?: string | null
  errorMessage: string
  failureReason: string
  payment: ExtendPayment | Nil
}

export function trackReverseInitiationError({
  payment,
  businessId,
  clientId,
  failureReason,
  errorMessage,
}: TrackReverseInitiationErrorParams): void {
  trackEvent(
    TrackingEvent.ADD_PAYMENT_V2_REVERSE_TRANSACTION_INITIATION_ERROR,
    {
      timestamp: Date.now(),
      invoiceId: getInvoiceId(payment),
      invoiceType: getInvoiceType(payment),
      businessId,
      customerId: clientId || payment?.clientId || null,
      paymentId: payment?.id || null,
      failureReason,
      errorMessage,
    },
  )
}

export interface TrackVoidSuccessParams {
  businessId: string
  clientId?: string | null
  payment: ExtendPayment | Nil
  processingDurationMs?: number
  transactionId: string
  voidAmount: number
}

export function trackVoidSuccess({
  payment,
  businessId,
  clientId,
  transactionId,
  voidAmount,
  processingDurationMs,
}: TrackVoidSuccessParams): void {
  trackEvent(TrackingEvent.ADD_PAYMENT_V2_VOID_SUCCESS, {
    timestamp: Date.now(),
    invoiceId: getInvoiceId(payment),
    invoiceType: getInvoiceType(payment),
    businessId,
    customerId: clientId || payment?.clientId || null,
    transactionId,
    paymentId: payment?.id || null,
    processingDurationMs,
    voidAmount,
  })
}

export interface TrackVoidFailureParams {
  businessId: string
  clientId?: string | null
  errorMessage?: string | null
  payment: ExtendPayment | Nil
  processingDurationMs?: number
  transactionId: string
  voidAmount: number
}

export function trackVoidFailure({
  payment,
  businessId,
  clientId,
  transactionId,
  voidAmount,
  errorMessage,
  processingDurationMs,
}: TrackVoidFailureParams): void {
  trackEvent(TrackingEvent.ADD_PAYMENT_V2_VOID_FAILURE, {
    timestamp: Date.now(),
    invoiceId: getInvoiceId(payment),
    invoiceType: getInvoiceType(payment),
    businessId,
    customerId: clientId || payment?.clientId || null,
    transactionId,
    paymentId: payment?.id || null,
    processingDurationMs,
    voidAmount,
    errorMessage: errorMessage || null,
  })
}
